/**
 * Subscribes to the company-scoped SSE feed and projects the events that
 * belong to the active meeting onto wrapper state:
 *
 *   - processing_stage    — drives the FinalizationProgress strip
 *   - speakers_identified — fills the identified-speakers badge list
 *   - streaming_stopped   — marks intentional WS close so the streaming
 *                           reconnect loop stops trying
 *   - streaming_fallback  — same, but with the "falling back to batch"
 *                           wrapper-state side effect
 *
 * Other event types are ignored — the broader notifications drawer is a
 * separate concern.
 *
 * Named `useRecordingMeetingEvents` (not `useMeetingEvents`) to disambiguate
 * from the unrelated `useMeetingEvents` in `hooks/useSSE.ts`, which projects
 * tracked-meeting phase updates with a different signature.
 */
import { SSE_EVENTS } from "@lexicanon/shared"
import { useEffect, useRef } from "react"
import { createReconnectingEventSource } from "../../../hooks/useSSE"
import type { TProcessingStage } from "../../FinalizationProgress"

interface IProps {
  meetingId: string
  companyId: string | null | undefined
  setProcessingStage: (stage: TProcessingStage | null) => void
  setIdentifiedSpeakers: (speakers: string[]) => void
  handleStreamingStopped: () => void
  handleStreamingFallback: () => void
}

const PROCESSING_STAGES: readonly TProcessingStage[] = [
  "uploading",
  "transcribing",
  "identifying_speakers",
  "analyzing",
  "complete",
]

function asProcessingStage(value: unknown): TProcessingStage | null {
  return PROCESSING_STAGES.find((stage) => stage === value) ?? null
}

export function useRecordingMeetingEvents(props: IProps): void {
  const {
    meetingId,
    companyId,
    setProcessingStage,
    setIdentifiedSpeakers,
    handleStreamingStopped,
    handleStreamingFallback,
  } = props

  // Keep the latest callbacks in refs so the SSE subscription doesn't tear down
  // and reconnect every render — only meetingId / companyId changes do that.
  const setProcessingStageRef = useRef(setProcessingStage)
  setProcessingStageRef.current = setProcessingStage
  const setIdentifiedSpeakersRef = useRef(setIdentifiedSpeakers)
  setIdentifiedSpeakersRef.current = setIdentifiedSpeakers
  const handleStreamingStoppedRef = useRef(handleStreamingStopped)
  handleStreamingStoppedRef.current = handleStreamingStopped
  const handleStreamingFallbackRef = useRef(handleStreamingFallback)
  handleStreamingFallbackRef.current = handleStreamingFallback

  useEffect(() => {
    if (!companyId) return

    const handleMessage = (event: MessageEvent<string>): void => {
      try {
        const parsed: unknown = JSON.parse(event.data)
        if (typeof parsed !== "object" || parsed === null) return
        const data = parsed as Record<string, unknown>
        if (data.meetingId !== meetingId) return
        if (data.type === SSE_EVENTS.PROCESSING_STAGE) {
          setProcessingStageRef.current(asProcessingStage(data.stage))
          if (data.stage === "complete") setTimeout(() => setProcessingStageRef.current(null), 2000)
        }
        if (data.type === SSE_EVENTS.SPEAKERS_IDENTIFIED && Array.isArray(data.speakers)) {
          setIdentifiedSpeakersRef.current(data.speakers.filter((s): s is string => typeof s === "string"))
        }
        if (data.type === SSE_EVENTS.STREAMING_STOPPED) handleStreamingStoppedRef.current()
        if (data.type === SSE_EVENTS.STREAMING_FALLBACK) handleStreamingFallbackRef.current()
      } catch {}
    }

    return createReconnectingEventSource(`/api/companies/${companyId}/events`, false, handleMessage)
  }, [meetingId, companyId])
}
